<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { default as PDivider } from 'primevue/divider'
import { default as PChart } from 'primevue/chart'
import { default as PTabView } from 'primevue/tabview'
import { default as PTabs } from 'primevue/tabs'
import { default as PTab } from 'primevue/tab'
import { default as PTabList } from 'primevue/tablist'
import { default as PTabPanels } from 'primevue/tabpanels'
import { default as PTabPanel } from 'primevue/tabpanel'

import type { TooltipItem } from 'chart.js'
import { useDashboardClassroomStore } from '@/presentation/stores/dashboard/dashboardClassroom.ts'
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'

// PROPS
defineProps<{
  schoolId: number
  classroomId: number
}>()

// STORES REFS
const { dashboardClassroomLoginHistory } = storeToRefs(useDashboardClassroomStore())

// LOCAL REFS
const chartData = ref()
const chartOptions = ref()

// COMPUTED
const setChartData = computed(() => {
  return {
    labels: [
      `Pas connecté depuis plus de 14 jours : ${dashboardClassroomLoginHistory.value?.moreThan14Days?.length}`,
      `Connecté il y a plus de 7 jours et moins de 14 jours : ${dashboardClassroomLoginHistory.value?.between7And14Days?.length}`,
      `Connecté(s) les 7 derniers jours : ${dashboardClassroomLoginHistory.value?.lessThan7Days?.length}`
    ],
    datasets: [
      {
        name: 'lessThan7Days',
        data: [
          dashboardClassroomLoginHistory.value?.moreThan14Days?.length,
          dashboardClassroomLoginHistory.value?.between7And14Days?.length,
          dashboardClassroomLoginHistory.value?.lessThan7Days?.length
        ],
        backgroundColor: ['#FF6259', '#EEC137', '#4CD07D'],
        hoverBackgroundColor: ['#FF6259', '#EEC137', '#4CD07D'],
        hoverOffset: 4
      }
    ]
  }
})

const setChartOptions = computed(() => {
  return {
    responsive: true, // Le graphique est responsive
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: true,
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue(
          '--bo-brand-secondary'
        ),
        titleColor: '#000000',
        bodyColor: '#000000',
        footerColor: '#000000',
        borderColor: getComputedStyle(document.documentElement).getPropertyValue(
          '--bo-brand-tertiary'
        ),
        borderWidth: 1,
        displayColors: false,
        callbacks: {
          title: function (tooltipItems: TooltipItem<'pie'>[]) {
            return tooltipItems[0].label
          },
          label: function () {
            return ''
          }
        }
      },
      legend: {
        display: true,
        align: 'start',
        position: 'left',
        fullSize: false,
        labels: {
          usePointStyle: true
        }
      }
    }
  }
})

// STORE METHODS
const { fetchDashboardClassroomLoginHistory, reset } = useDashboardClassroomStore()

// METHODS

// WATCHERS

// LIFECYCLE HOOKS
onMounted(async () => {
  await fetchDashboardClassroomLoginHistory()
  chartData.value = setChartData.value
  chartOptions.value = setChartOptions.value
})

onUnmounted(() => {
  reset()
})
</script>

<template>
  <div class="col-span-12 flex flex-col gap-4">
    <c-base-block title="Assiduité des élèves">
      <div class="grid grid-cols-12 gap-4">
        <!-- Chart -->
        <div class="col-span-5 flex items-start justify-start">
          <p-chart type="pie" :data="chartData" :options="chartOptions" class="w-full" />
        </div>
        <!-- Divider -->

        <p-divider layout="vertical" class="col-span-1 w-auto" />
        <!-- List -->

        <div class="col-span-6">
          <p-tabs value="1">
            <p-tab-list>
              <p-tab value="1"> Plus de 14 jours </p-tab>
              <p-tab value="2"> Entre 7 et 14 jours </p-tab>
              <p-tab value="3"> Moins de 7 jours </p-tab>
            </p-tab-list>
            <p-tab-panels>
              <p-tab-panel value="1" header="Plus de 14 jours">
                <ul class="list-disc columns-3 overflow-x-auto">
                  <li
                    v-for="student in dashboardClassroomLoginHistory?.moreThan14Days"
                    :key="student.identifier"
                    class="w-6/12 flex-none"
                    data-maskable
                  >
                    {{ student.fullName }}
                  </li>
                </ul>
              </p-tab-panel>
              <p-tab-panel value="1" header="Entre 7 et 14 jours">
                <ul class="list-disc columns-3 overflow-x-auto">
                  <li
                    v-for="student in dashboardClassroomLoginHistory?.between7And14Days"
                    :key="student.identifier"
                    class="w-6/12 flex-none"
                    data-maskable
                  >
                    {{ student.fullName }}
                  </li>
                </ul>
              </p-tab-panel>
              <p-tab-panel value="1" header="Moins de 7 jours">
                <ul class="list-disc columns-3 overflow-x-auto">
                  <li
                    v-for="student in dashboardClassroomLoginHistory?.lessThan7Days"
                    :key="student.identifier"
                    class="w-6/12 flex-none"
                    data-maskable
                  >
                    {{ student.fullName }}
                  </li>
                </ul>
              </p-tab-panel>
            </p-tab-panels>
          </p-tabs>
        </div>
      </div>
    </c-base-block>
  </div>
</template>
